import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalBody,
} from 'reactstrap';
import PageBreadcrumb from '../../Components/Common/PageBreadcrumb';
import DataTable from 'react-data-table-component';
import ActionButton from '../../Components/Common/ActionButton';
import DeleteModal from '../../Components/Common/DeleteModal';
import ListPager from '../../Components/Common/ListPager';
import {
  ActiveFilterChips,
  ClearFiltersButton,
  ListSearch,
} from '../../Components/Common/ListFilters';
import { EmptyState, SkeletonRows } from '../../Components/Common/ListStates';
import notify, { MESSAGES } from '../../utils/notify';
import '../TenderOpening/components/TenderOpening.css';
import axios from 'axios';
import { api } from '../../config';
import { getAuthToken } from '../../auth/tokenvalidation';
import { FormGrid, TextAreaField, TextField } from '../../Components/Form';

const PaymentCurrencyList = () => {
  const token = getAuthToken();

  const [data, setData] = useState({ items: [] });
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteModalMulti, setDeleteModalMulti] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [modalPaymentCurrency, setModalPaymentCurrency] = useState(false);
  const [paymentCurrencyFormData, setPaymentCurrencyFormData] = useState({
    currencyName: null,
    currencyDescription: null,
    currencyCode: null,
    id: null,
  });

  const [filterText, setFilterText] = useState('');
  const [search, setSearch] = useState('');
  const requestRef = useRef(0);
  const [loading, setLoading] = useState(false);
  const startEntry = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endEntry = Math.min(currentPage * rowsPerPage, totalRows);

  const handleDeleteModal = id => {
    const selectedRow = data.items.find(item => item.id === id);
    setInvoice(selectedRow);
    setDeleteModal(true);
  };

  const handleDeleteInvoice = async () => {
    if (invoice) {
      try {
        const response = await fetch(
          `${api.API_URL}/paymentcurrency/${invoice.id}`,
          {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.message || `HTTP error! status: ${response.status}`
          );
        }

        notify.success('Payment Currency deleted successfully.');
        setDeleteModal(false);
        setInvoice(null);
        fetchData(currentPage, rowsPerPage, token, search);
      } catch (err) {
        notify.apiError(err, 'delete payment currency');
      }
    }
  };

  const deleteMultiple = () => {
    // Implement multiple delete functionality
    // This would typically involve deleting selected items
    // For now, just close the modal
    setDeleteModalMulti(false);
  };

  const columns = [
    {
      id: 'currencyName',
      name: 'Currency Name',
      selector: row => row.currencyName,
      sortable: true,
      width: '30%',
      cell: row => (
        <span className='to-title' title={String(row.currencyName ?? '')}>
          {row.currencyName ?? '-'}
        </span>
      ),
    },

    {
      id: 'currencyCode',
      name: 'Currency Code',
      selector: row => row.currencyCode,
      sortable: true,
      width: '15%',
      cell: row => row.currencyCode ?? '-',
    },

    {
      id: 'currencyDescription',
      name: 'Currency Description',
      selector: row => row.currencyDescription,
      sortable: true,
      width: '40%',
      cell: row => (
        <span title={String(row.currencyDescription ?? '')}>
          {row.currencyDescription ?? '-'}
        </span>
      ),
    },

    {
      name: <span className='text-center w-100'>Action</span>,
      width: '15%',
      center: true,
      cell: row => (
        <div className='d-flex gap-1 justify-content-center w-100'>
          <ActionButton
            variant='icon'
            size='sm'
            icon='ri-edit-line'
            label='Edit'
            onClick={() => handleUpdateModal(row.id)}
          />
          <ActionButton
            variant='icon'
            size='sm'
            danger
            icon='ri-delete-bin-line'
            label='Delete'
            confirm={false}
            onClick={() => handleDeleteModal(row.id)}
          />
        </div>
      ),
    },
  ];

  document.title = 'Data Payment Currency';

  const handleChange = e => {
    const { name, value } = e.target;
    setPaymentCurrencyFormData({ ...paymentCurrencyFormData, [name]: value });
  };

  // Debounce typing so each keystroke is not a request.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(filterText.trim());
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [filterText]);

  useEffect(() => {
    fetchData(currentPage, rowsPerPage, token, search);
  }, [currentPage, rowsPerPage, token, search]);

  const handleFilterChange = e => {
    setFilterText(e.target.value);
    // Clearing applies at once; typing waits for the debounce.
    if (!e.target.value) {
      setSearch('');
      setCurrentPage(1);
    }
  };

  const clearFilter = () => {
    setFilterText('');
    setSearch('');
    setCurrentPage(1);
  };

  const filterChips = [
    filterText && {
      key: 'search',
      label: 'Search',
      value: filterText,
      onRemove: () => handleFilterChange({ target: { value: '' } }),
    },
  ].filter(Boolean);
  const hasFilters = filterChips.length > 0;

  const fetchData = async (currentPage, rowsPerPage, token, search) => {
    const reqId = ++requestRef.current;
    setLoading(true);
    try {
      const response = await fetch(
        `${api.API_URL}/paymentcurrency?PageNumber=${currentPage}&PageSize=${rowsPerPage}${search ? `&Search=${encodeURIComponent(search)}` : ''}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          credentials: 'same-origin',
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (reqId !== requestRef.current) return; // stale response

      // Handle different possible response structures
      const items =
        result?.data?.items || result?.items || result?.data || result || [];
      const totalItems =
        result?.data?.totalItems || result?.totalItems || items.length;

      setData({
        items: Array.isArray(items) ? items : [],
      });
      setTotalRows(totalItems);
    } catch (error) {
      if (reqId !== requestRef.current) return;
      notify.apiError(error, 'load data');
      setData({ items: [] });
      setTotalRows(0);
    } finally {
      if (reqId === requestRef.current) setLoading(false);
    }
  };

  const handlePageChange = useCallback(page => {
    setCurrentPage(page);
  }, []);

  const handlePerRowsChange = useCallback(newPerPage => {
    setRowsPerPage(newPerPage);
    setCurrentPage(1);
  }, []);

  const handleNewModal = () => {
    setPaymentCurrencyFormData({
      currencyName: null,
      currencyDescription: null,
      currencyCode: null,
      id: null,
    });
    paymentCurrencyModal();
  };

  const handleUpdateModal = async (mid = null) => {
    await fetchPaymentCurrencyDetail(mid);
    paymentCurrencyModal();
  };

  const paymentCurrencyModal = () => {
    setModalPaymentCurrency(!modalPaymentCurrency);
  };

  const selectedFieldsPaymentCurrency = {
    currencyName: paymentCurrencyFormData.currencyName,
    currencyDescription: paymentCurrencyFormData.currencyDescription,
    currencyCode: paymentCurrencyFormData.currencyCode,
  };

  const handleAddDataPaymentCurrency = async () => {
    try {
      await axios.post(
        `${api.API_URL}/paymentcurrency`,
        selectedFieldsPaymentCurrency,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      notify.success('Payment Currency added successfully.');
    } catch (error) {
      notify.apiError(error, 'save data');
    } finally {
      fetchData(currentPage, rowsPerPage, token, search);
    }
  };

  const fetchPaymentCurrencyDetail = async mid => {
    try {
      const response = await axios.get(
        `${api.API_URL}/paymentcurrency/${mid}`,
        {
          withXSRFToken: true,
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: `application/json`,
          },
        }
      );
      const data = response?.data?.data ?? response?.data ?? response;
      setPaymentCurrencyFormData({
        currencyName: data.currencyName || null,
        currencyDescription: data.currencyDescription || null,
        currencyCode: data.currencyCode || null,
        id: data.id || null,
      });
    } catch (error) {
      // Handle error silently or show appropriate message
      notify.apiError(error, 'fetch payment currency details');
    }
  };

  const handleUpdDataPaymentCurrency = async mid => {
    try {
      await axios.put(
        `${api.API_URL}/paymentcurrency/${mid}`,
        selectedFieldsPaymentCurrency,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      notify.success(MESSAGES.updated);
    } catch (error) {
      notify.apiError(error, 'save data');
    } finally {
      fetchData(currentPage, rowsPerPage, token, search);
    }
  };

  return (
    <React.Fragment>
      <div className='page-content'>
        <DeleteModal
          show={deleteModal}
          onDeleteClick={() => handleDeleteInvoice()}
          onCloseClick={() => setDeleteModal(false)}
          title='Delete this payment currency?'
          message='It will be permanently deleted. This cannot be undone.'
          subject={invoice?.currencyName}
          confirmLabel='Delete payment currency'
        />
        <DeleteModal
          show={deleteModalMulti}
          onDeleteClick={() => {
            deleteMultiple();
            setDeleteModalMulti(false);
          }}
          onCloseClick={() => setDeleteModalMulti(false)}
        />
        <Container fluid>
          <div className='to-page email-wrapper d-lg-flex gap-1 mx-n4 mt-n4 p-1'>
            <div className='email-content minimal-border'>
              <div className='p-4'>
                <div className='to-hero mb-4'>
                  <PageBreadcrumb
                    fallback={{
                      label: 'Payment Currency',
                      to: '/apps-payment-currency/view',
                    }}
                  />

                  <div className='d-flex align-items-center gap-3 flex-wrap'>
                    <span className='to-hero-icon'>
                      <i
                        className='ri-money-dollar-circle-line'
                        aria-hidden='true'
                      />
                    </span>
                    <div className='flex-grow-1'>
                      <h4 className='to-hero-title'>Payment Currency</h4>
                      <p className='to-hero-subtitle'>
                        Maintain the currencies available for payment.
                      </p>
                    </div>
                    <div className='d-flex gap-2 flex-wrap'>
                      <button
                        type='button'
                        className='to-hero-btn'
                        onClick={() =>
                          fetchData(currentPage, rowsPerPage, token, search)
                        }
                      >
                        <i className='ri-refresh-line' aria-hidden='true' />
                        Refresh
                      </button>
                      <button
                        type='button'
                        className='to-hero-btn to-hero-btn--solid'
                        onClick={() => handleNewModal()}
                      >
                        <i className='ri-add-line' aria-hidden='true' />
                        Create New Payment Currency
                      </button>
                    </div>
                  </div>
                </div>

                <div className='to-panel'>
                  <div className='to-toolbar'>
                    <ListSearch
                      value={filterText}
                      onChange={handleFilterChange}
                      placeholder='Search payment currencies...'
                      ariaLabel='Search payment currencies'
                    />
                    <ClearFiltersButton
                      count={filterChips.length}
                      onClick={clearFilter}
                    />
                    <span className='to-toolbar-meta'>
                      {loading
                        ? 'Loading...'
                        : `Showing ${startEntry}–${endEntry} of ${totalRows} payment currencies`}
                    </span>
                  </div>

                  <ActiveFilterChips chips={filterChips} />

                  <div className='to-table-wrap'>
                    <DataTable
                      columns={columns}
                      data={data.items}
                      highlightOnHover
                      persistTableHead
                      defaultSortFieldId='currencyName'
                      defaultSortAsc
                      progressPending={loading}
                      progressComponent={<SkeletonRows />}
                      noDataComponent={
                        <EmptyState
                          title='No payment currency found'
                          emptyHint='No payment currencies have been registered yet. Currencies added here become selectable as payment currencies.'
                          filteredHint='No payment currency matches the current filters.'
                          hasFilters={hasFilters}
                          onClear={clearFilter}
                          action={{
                            label: 'Create New Payment Currency',
                            onClick: handleNewModal,
                          }}
                        />
                      }
                      customStyles={{
                        headCells: {
                          style: { paddingLeft: '12px', paddingRight: '12px' },
                        },
                        cells: {
                          style: {
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            wordBreak: 'break-word',
                          },
                        },
                        table: { style: { width: '100%' } },
                      }}
                    />
                  </div>

                  <ListPager
                    currentPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    totalRows={totalRows}
                    isLoading={loading}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handlePerRowsChange}
                  />
                </div>
              </div>
            </div>
          </div>

          <Modal
            isOpen={modalPaymentCurrency}
            role='dialog'
            autoFocus={true}
            centered
            id='addPaymentCurrencyModal'
            toggle={paymentCurrencyModal}
            data-bs-backdrop='static'
            size='md'
          >
            <ModalHeader
              className='bg-soft-info'
              toggle={() => {
                setModalPaymentCurrency(!modalPaymentCurrency);
              }}
            >
              <span className='modal-title text-info fw-bold'>
                {paymentCurrencyFormData.id !== null
                  ? 'Edit Payment Currency'
                  : 'Add Payment Currency'}
              </span>
            </ModalHeader>

            <ModalBody>
              <FormGrid columns={1}>
                <TextField
                  label='Currency name'
                  name='currencyName'
                  placeholder='e.g. Indonesian Rupiah'
                  value={paymentCurrencyFormData.currencyName}
                  onChange={handleChange}
                />
                <TextField
                  label='Currency code'
                  name='currencyCode'
                  placeholder='e.g. IDR'
                  value={paymentCurrencyFormData.currencyCode}
                  onChange={handleChange}
                />
                <TextAreaField
                  label='Currency description'
                  name='currencyDescription'
                  rows={5}
                  value={paymentCurrencyFormData.currencyDescription}
                  onChange={handleChange}
                />
              </FormGrid>
            </ModalBody>

            <ModalFooter>
              <ActionButton
                variant='secondary'
                size='sm'
                icon='ri-close-line'
                onClick={() => {
                  setModalPaymentCurrency(!modalPaymentCurrency);
                }}
              >
                Close
              </ActionButton>
              <ActionButton
                variant='primary'
                size='sm'
                icon='ri-save-line'
                onClick={async () => {
                  await (paymentCurrencyFormData.id !== null
                    ? handleUpdDataPaymentCurrency(paymentCurrencyFormData.id)
                    : handleAddDataPaymentCurrency());
                  setModalPaymentCurrency(false);
                }}
              >
                {paymentCurrencyFormData.id !== null ? 'Update' : 'Save'}
              </ActionButton>
            </ModalFooter>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default PaymentCurrencyList;
